import type { CSSProperties } from 'react'

import type { CachedInquiry, CachedInternalMeeting } from '../../../offline/data-db'
import type { TourState } from '../../inspections/lib/list-filters'
import { dayLabel, hourLabel, shortDayLabel } from '../lib/agenda-labels'
import {
  agendaItemsOf,
  fittingHourPx,
  gutterOf,
  hoursOf,
  itemDay,
  itemKey,
  placeDay,
} from '../lib/agenda-layout'
import { AppointmentCard } from './AppointmentCard'
import { InternalMeetingCard } from './InternalMeetingCard'
import s from './WeekGrid.module.css'

/**
 * La vue Semaine : une colonne par jour ouvré (lundi → samedi), une ligne par heure, les cartes
 * posées sur la gouttière — la maquette validée, au défaut de chevauchement près
 * (`agenda-layout.ts`).
 *
 * ⚠️ **La gouttière est dérivée de la semaine entière, pas de chaque jour** : six colonnes dont
 * les heures ne s'alignent pas ne sont plus un agenda, et un rendez-vous de 7 h se lirait à la
 * hauteur de celui de 9 h de la colonne voisine.
 *
 * ⚠️ …mais de la semaine **rendue**, soit les seuls jours de `days`. La plage lue en cache va
 * jusqu'au dimanche, que la grille n'a pas de colonne pour montrer (`weekRangeOf`) : un unique
 * rendez-vous dominical à 6 h 30 élargirait la gouttière des six autres jours, qui gagneraient
 * deux bandes d'heures vides pendant que toutes leurs cartes rétrécissent. L'écran reprend ces
 * rendez-vous **hors grille**, sous la grille ; ils ne doivent pas en dicter les heures.
 */
export interface WeekGridProps {
  /** Les six jours affichés, dans l'ordre. */
  days: readonly string[]
  /** La semaine lue en cache, tous jours confondus — la grille répartit elle-même. */
  inquiries: readonly CachedInquiry[]
  /**
   * Les points internes de l'équipe sur la même semaine (#319). Posés **avec** les rendez-vous,
   * pas à côté : ils occupent la même équipe, donc la même gouttière et les mêmes colonnes de
   * chevauchement — un brief à 9 h et un contrôle à 9 h 30 se partagent la largeur du jour.
   */
  meetings: readonly CachedInternalMeeting[]
  /**
   * L'état de tournée de chaque contrôle affiché, par identifiant. Construit une fois par
   * l'écran : la file d'envoi qui le décide porte tous les dépôts de la vie de la tablette, et
   * l'interroger par carte la rebalaierait autant de fois qu'il y a de rendez-vous.
   */
  states: ReadonlyMap<string, TourState>
  /** Aujourd'hui à Paris : ce qui le précède est passé, et s'estompe (#577). */
  today: string
  /**
   * Les créneaux faits (`isDone`, #577), par `itemKey` — calculés une fois par l'écran, comme
   * l'état de tournée.
   */
  done: ReadonlySet<string>
  onOpen: (inquiry: CachedInquiry) => void
}

export function WeekGrid({
  days,
  inquiries,
  meetings,
  states,
  today,
  done,
  onOpen,
}: WeekGridProps) {
  // Ce que la grille montre vraiment — cf. le second avertissement ci-dessus.
  const shown = agendaItemsOf(inquiries, meetings).filter((item) =>
    days.includes(itemDay(item) ?? ''),
  )
  const gutter = gutterOf(shown)
  const hours = hoursOf(gutter)

  return (
    <div
      className={s.grid}
      // Une hauteur d'heure pour toute la semaine, comme la gouttière : les six colonnes
      // s'alignent heure par heure.
      style={{ '--agenda-hour-fit': `${String(fittingHourPx(shown))}px` } as CSSProperties}
    >
      <div className={s.hours} aria-hidden>
        {hours.map((hour) => (
          <span key={hour} className={s.hour}>
            {hourLabel(hour)}
          </span>
        ))}
      </div>
      {days.map((day) => {
        const past = day < today

        return (
          // Une section nommée par son jour : au lecteur d'écran, les cartes d'une colonne sont
          // alors annoncées sous « samedi 8 août » — la position à l'écran ne dit rien.
          <section key={day} aria-label={past ? `${dayLabel(day)}, passé` : dayLabel(day)}>
            <h3 className={s.dayHead} data-past={past ? '' : undefined}>
              {shortDayLabel(day)}
            </h3>
            <div className={s.slots} data-past={past ? '' : undefined}>
              {hours.map((hour) => (
                <div key={hour} className={s.slot} aria-hidden />
              ))}
              {placeDay(
                shown.filter((item) => itemDay(item) === day),
                gutter,
              ).map(({ item, ...placement }) =>
                item.kind === 'appointment' ? (
                  <AppointmentCard
                    key={itemKey(item)}
                    inquiry={item.inquiry}
                    placement={placement}
                    // Le repli sur le statut en cache est celui de `tourStateOf` lui-même devant
                    // une demande jamais déposée : la carte est construite sur les mêmes lignes.
                    state={states.get(item.inquiry.id) ?? item.inquiry.status}
                    done={done.has(itemKey(item))}
                    onOpen={onOpen}
                  />
                ) : (
                  <InternalMeetingCard
                    key={itemKey(item)}
                    meeting={item.meeting}
                    placement={placement}
                    done={done.has(itemKey(item))}
                  />
                ),
              )}
            </div>
          </section>
        )
      })}
    </div>
  )
}
